import React, { useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import styles from './css/index.module.css';
import WebViewer from '@pdftron/webviewer';
import { setupWebViewerPreviewInstance } from '../../utils/webViewerFitWidth';
import {
    ChevronRight20Regular,
    MoreVertical16Regular,
    Delete16Regular,
    Info16Regular,
    Dismiss20Regular,
    ArrowDownload16Regular,
    Open16Regular,
    Link16Regular,
} from '@fluentui/react-icons';
import { Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, OverlayDrawer } from '@fluentui/react-components';
import { DeleteBulkFiles, GetIRLDocData, GetPopulateDownloadBy, GetDocumentShareLink, CreateDocumentShareLink, GetAuthenticatedDocUrl } from '../../redux/documentsSlice';
import {
    downloadVdrDocumentFile,
    createVdrDocumentObjectUrl,
    resolveVdrWorkstreamId,
} from '../../utils/vdrDocumentDownloadUtils';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { getYouTubeId, isAudioFile } from '../../utils/commonUtils';
import {
    getFileExtensionFromFile,
    getNativeTextPreviewMode,
    EXCEL_TOO_LARGE_PREVIEW_MESSAGE,
    getWebViewerIframeOptions,
    isNativeTextPreviewFile,
    isPDFTronSupportedFile,
    supportsPdfCitationHighlight,
} from '../../utils/filePreviewUtils';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import FileInfoSidebar from './FileInfoSidebar';
import { QuickAddMenu } from '../CommonComponents/Header';
import { NotepadSidebar } from '../CommonComponents/HeaderSidebars';
import TaskFormSidebar from '../TaskManagement/TaskFormSidebar';
import { PostInformationRequestSidebar } from '../IRL';
import { ObservationFormSidebar } from '../Observations';
import { runCitationHighlightAsync } from './pdfTronCitationHighlight';
import {
    classifyDocumentLoadError,
    fetchDocumentProtectionCheck,
    getProtectedDocumentMessage,
} from '../../utils/protectedDocumentUtils';

const FileViewerModal = ({
    openModal,
    setOpenModal,
    selectedFile,
    setSelectedFile,
    setSelectedFileIndex,
    folderFiles,
    selectedFileIndex,
    workstream,
    citationHighlight = null,
    onCitationHighlightConsumed,
}) => {
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const [isVideo, setIsVideo] = React.useState(false);
    const [isAudio, setIsAudio] = React.useState(false);
    const [isLinkFile, setIsLinkFile] = React.useState(false);
    const [pdfLoading, setPdfLoading] = React.useState(false);
    const [fileUrl, setFileUrl] = React.useState(null);
    const [unsupportedFile, setUnsupportedFile] = React.useState(false);
    const [previewErrorMessage, setPreviewErrorMessage] = React.useState(null);
    const [isNativeTextPreview, setIsNativeTextPreview] = React.useState(false);
    const [nativeTextContent, setNativeTextContent] = React.useState('');
    const [nativeTextMode, setNativeTextMode] = React.useState('plain');
    const [openFileInfo, setOpenFileInfo] = React.useState(false);
    const viewer = React.useRef(null);
    const instanceRef = React.useRef(null);
    const blobObjectUrlRef = React.useRef(null);
    const deleteModalRef = React.useRef();
    const citationHighlightRef = useRef(null);
    const dispatch = useDispatch();

    useLayoutEffect(() => {
        citationHighlightRef.current = citationHighlight || null;
    }, [citationHighlight]);
    const isProjectAdmin = selectedProject?.vdr?.vdr_admin === user?.id || selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);
    const filePath = selectedFile?.filePath?.split('/');
    const [notepadModal, setNotepadModal] = React.useState(false);
    const [questionModal, setQuestionModal] = React.useState(false);
    const [obsModal, setObsModal] = React.useState(false);
    const [taskModal, setTaskModal] = React.useState(false);
    const [shareUrlLoading, setShareUrlLoading] = React.useState(false);

    const handleShareUrl = React.useCallback(async () => {
        if (!selectedFile?.id) return;
        setShareUrlLoading(true);
        try {
            let payload;
            try {
                payload = await dispatch(GetDocumentShareLink(selectedFile.id)).unwrap();
            } catch {
                payload = await dispatch(CreateDocumentShareLink(selectedFile.id)).unwrap();
            }
            const fullUrl = `${window.location.origin}/shared/document/${payload?.share_id}`;
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(fullUrl);
            } else {
                const ta = document.createElement('textarea');
                ta.value = fullUrl;
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            toast.success('Share URL copied to clipboard', { position: 'top-right' });
        } catch (err) {
            const msg = err?.detail || err?.message || 'Failed to get share URL';
            toast.error(msg, { position: 'top-right' });
        } finally {
            setShareUrlLoading(false);
        }
    }, [selectedFile?.id, dispatch]);

    const handleClose = () => {
        setOpenModal(false);
        setSelectedFile();
        setSelectedFileIndex && setSelectedFileIndex();
        setFileUrl(null);
        setUnsupportedFile(false);
        setPreviewErrorMessage(null);
        // Clean up PDFTron instance - just clear the reference
        instanceRef.current = null;
        citationHighlightRef.current = null;
        onCitationHighlightConsumed?.();
        setOpenFileInfo(false);
        setIsLinkFile(false);
        setIsVideo(false);
        setIsAudio(false);
    };

    const getFileExtension = useCallback(file => getFileExtensionFromFile(file), []);

    const checkIsLinkFile = useCallback(file => {
        // If document exists, it's an uploaded file
        // If document_link exists (and document doesn't), it's a link to file
        if (file?.document_link && !file?.document) {
            // It's a link file - but exclude YouTube (handled separately as video)
            const link = file.document_link;
            const isYouTube = link.includes('youtube') || link.includes('youtu.be');
            return !isYouTube; // All links are link files except YouTube
        }
        return false;
    }, []);

    const handleOpenLink = () => {
        if (selectedFile?.document_link) {
            const { safeOpenUrl } = require('../../utils/urlValidation');
            safeOpenUrl(selectedFile.document_link, '_blank');
        }
    };

    const handleReadDoc = useCallback(
        fileData => {
            if (!fileData) return;
            const id = projectUsersAll?.find(item => item?.user_id === user?.id)?.value?.group_member_id;
            const isAlreadyPresent = fileData?.read_by?.find(item => item === id);
            if (!isAlreadyPresent) {
                dispatch(GetIRLDocData({ id: fileData?.id, workstream: workstream?.id }));
            }
        },
        [projectUsersAll, user?.id, dispatch, workstream?.id],
    );

    const handleNavigateFiles = async direction => {
        // Clean up current PDFTron instance before navigating
        instanceRef.current = null;

        // Reset states first - this ensures old content is cleared
        setFileUrl(null);
        setUnsupportedFile(false);
        setPreviewErrorMessage(null);
        setIsVideo(false);
        setIsAudio(false);
        setIsLinkFile(false);
        setPdfLoading(true);

        // Wait a tick to ensure state is reset before setting new file
        await new Promise(resolve => setTimeout(resolve, 0));

        if (direction === 'prev') {
            const prevData = folderFiles[selectedFileIndex - 1];
            setSelectedFileIndex(selectedFileIndex - 1);
            setSelectedFile(prevData);
            handleReadDoc(prevData);
            // Check if previous file is a link
            setIsLinkFile(checkIsLinkFile(prevData));
        } else {
            const nextData = folderFiles[selectedFileIndex + 1];
            setSelectedFileIndex(selectedFileIndex + 1);
            setSelectedFile(nextData);
            handleReadDoc(nextData);
            // Check if next file is a link
            setIsLinkFile(checkIsLinkFile(nextData));
        }
    };
    const handleCheckVideo = useCallback(
        row => {
            if (row?.document_link?.includes('youtube') || row?.document_link?.includes('youtu.be')) {
                if (workstream?.id) {
                    handleReadDoc(row);
                }
                return true;
            }
            let ext = row?.document_name?.split('.').pop();
            // eslint-disable-next-line default-case
            switch (ext?.toLowerCase()) {
                case 'm4v':
                case 'avi':
                case 'mpg':
                case 'mov':
                case 'mp5':
                case 'mp4':
                    if (workstream?.id) {
                        handleReadDoc(row);
                    }
                    return true;
            }
            return false;
        },
        [workstream?.id, handleReadDoc],
    );
    const handleDownloadFile = async () => {
        const isMailAttachment = selectedFile?.source === 'project_mail_attachment';
        const isTentChatAttachment = selectedFile?.source === 'tent_chat_attachment';
        const canOriginal =
            selectedFile?.can_download_original || isProjectAdmin || isMailAttachment || isTentChatAttachment;
        const canWatermarked = selectedFile?.can_download_watermarked;

        if (canWatermarked && !canOriginal && instanceRef?.current) {
            handleDownloadWatermarkedFile();
            dispatch(GetPopulateDownloadBy({ id: selectedFile?.id, wsId: workstream?.id }));
            return;
        }

        if (!canOriginal) {
            return;
        }

        if (isTentChatAttachment && selectedFile?.document) {
            try {
                let url = selectedFile.document;
                if (url.includes('.blob.core.windows.net')) {
                    url = await dispatch(GetAuthenticatedDocUrl(url)).unwrap();
                }
                const link = document.createElement('a');
                link.href = url;
                link.download = selectedFile.document_name || 'download';
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                link.remove();
                return;
            } catch (error) {
                console.error('Tent chat attachment download failed:', error);
                toast.error('Download failed');
                return;
            }
        }

        const ok = await downloadVdrDocumentFile(dispatch, selectedFile, { workstream });
        if (!ok) {
            toast.error('Download failed');
        }
    };
    const handleDownloadWatermarkedFile = () => {
        if (!instanceRef.current) return;
        const { Core } = instanceRef.current;
        Core.documentViewer.setWatermark({
            diagonal: {
                fontSize: 40, // or even smaller size
                fontFamily: 'sans-serif',
                color: '#003048',
                opacity: 50, // from 0 to 100
                text: 'www.lens-dd.ai',
            },
        });
        instanceRef?.current?.UI?.downloadPdf();
    };
    const handleDeleteFile = async () => {
        await dispatch(DeleteBulkFiles({ items: [selectedFile?.id], ws_id: workstream?.id }));
        handleClose();
    };
    useEffect(() => {
        if (fileUrl && !isLinkFile && !isVideo && !isAudio) {
            const fileExtension = getFileExtension(selectedFile);

            if (isNativeTextPreviewFile(selectedFile)) {
                setUnsupportedFile(false);
                setIsNativeTextPreview(true);
                setNativeTextMode(getNativeTextPreviewMode(fileExtension));
                setPdfLoading(true);

                let cancelled = false;
                fetch(fileUrl)
                    .then(response => {
                        if (!response.ok) {
                            throw new Error('Failed to load file content.');
                        }
                        return response.text();
                    })
                    .then(text => {
                        if (cancelled) return;
                        setNativeTextContent(text);
                        setPdfLoading(false);
                        if (workstream) {
                            handleReadDoc(selectedFile);
                        }
                    })
                    .catch(error => {
                        if (cancelled) return;
                        console.error('Native text preview load error:', error);
                        setNativeTextContent('');
                        setUnsupportedFile(true);
                        setIsNativeTextPreview(false);
                        setPdfLoading(false);
                    });

                return () => {
                    cancelled = true;
                };
            }

            setIsNativeTextPreview(false);
            setNativeTextContent('');

            // Check if file is supported by PDFTron
            if (!isPDFTronSupportedFile(selectedFile)) {
                console.warn('File type not supported by PDFTron:', getFileExtension(selectedFile));
                setUnsupportedFile(true);
                setPdfLoading(false);
                return;
            }

            setUnsupportedFile(false);
            setPreviewErrorMessage(null);

            // Always create a new instance when fileUrl changes to ensure clean state
            // Clear any existing instance first
            if (instanceRef?.current) {
                instanceRef.current = null;
            }
            // When using webviewerServerURL, set TRN_ALLOWED_ORIGINS on the WebViewer Server (Azure App Service)
            // to your app origin(s), e.g. https://yourapp.com,https://localhost:3000
            let disposed = false;

            const blockProtectedPreview = reason => {
                const message = getProtectedDocumentMessage(reason);
                toast.warning(message, {
                    position: 'top-right',
                    toastId: `protected-doc-${selectedFile?.id}`,
                });
                setPreviewErrorMessage(message);
                setUnsupportedFile(true);
                setPdfLoading(false);
            };

            const initWebViewer = async () => {
                const protection = await fetchDocumentProtectionCheck(fileUrl, fileExtension);
                if (disposed) return;

                if (protection.protected) {
                    blockProtectedPreview(protection.reason);
                    return;
                }

                WebViewer.Iframe(
                    getWebViewerIframeOptions({
                        fileUrl,
                        extension: fileExtension,
                        filename: selectedFile?.document_name,
                    }),
                    viewer.current,
                )
                    .then(instance => {
                        if (disposed) return;
                        instanceRef.current = instance;

                        setupWebViewerPreviewInstance(instance, {
                            extension: fileExtension,
                            fileUrl,
                            filename: selectedFile?.document_name,
                            isDisposed: () => disposed,
                            onReady: () => setPdfLoading(false),
                            onDocumentLoadError: error => {
                                console.error('PDFTron document load error:', error);
                                const kind = classifyDocumentLoadError(error);
                                if (kind === 'protected') {
                                    blockProtectedPreview('generic_protected');
                                    return;
                                }
                                if (kind === 'excel_too_large') {
                                    instance.UI.closeElements(['errorModal']);
                                    setPreviewErrorMessage(EXCEL_TOO_LARGE_PREVIEW_MESSAGE);
                                    setUnsupportedFile(true);
                                    setPdfLoading(false);
                                }
                            },
                        onDocumentLoaded: () => {
                            if (workstream) {
                                handleReadDoc(selectedFile);
                            }
                            const h = citationHighlightRef.current;
                            if (
                                supportsPdfCitationHighlight(fileExtension) &&
                                h?.text &&
                                String(selectedFile?.id) === String(h.fileId)
                            ) {
                                window.setTimeout(() => {
                                    void runCitationHighlightAsync(instance, h.text, {
                                        pageOneBased: h.pageOneBased ?? null,
                                    })
                                        .catch(err => console.warn('Citation highlight error:', err))
                                        .finally(() => {
                                            citationHighlightRef.current = null;
                                            onCitationHighlightConsumed?.();
                                        });
                                }, 450);
                            }
                        },
                        });
                    })
                    .catch(err => {
                        if (disposed) return;
                        console.error('PDFTron initialization error:', err);
                        setPdfLoading(false);
                    });
            };

            initWebViewer();

            return () => {
                disposed = true;
            };
        }
    }, [fileUrl, isLinkFile, isVideo, isAudio, selectedFile, getFileExtension, handleReadDoc, workstream, onCitationHighlightConsumed]);

    useEffect(() => {
        if (selectedFile) {
            const efectFunction = async () => {
                if (blobObjectUrlRef.current) {
                    URL.revokeObjectURL(blobObjectUrlRef.current);
                    blobObjectUrlRef.current = null;
                }
                // Reset states
                setPdfLoading(true);
                setIsVideo(false);
                setIsAudio(false);
                setIsLinkFile(false);
                setUnsupportedFile(false);
                setPreviewErrorMessage(null);
                setIsNativeTextPreview(false);
                setNativeTextContent('');
                setFileUrl(null);

                // Clean up previous PDFTron instance
                // Just clear the reference - don't call dispose to avoid unmount errors
                instanceRef.current = null;

                const isVideoFile = handleCheckVideo(selectedFile);
                setIsVideo(isVideoFile);

                // Check if it's a link file (external link, not YouTube, not blob)
                const isLink = checkIsLinkFile(selectedFile);
                setIsLinkFile(isLink);

                if (isLink) {
                    // Don't load PDFTron for link files
                    setPdfLoading(false);
                    return;
                }

                if (selectedFile?.document_link?.includes('youtube')) {
                    setFileUrl(selectedFile?.document_link);
                } else {
                    // Only use document (actual file) for PDFTron, never document_link
                    // document_link is only for external links which should show the button
                    if (selectedFile?.document) {
                        const documentUrl = selectedFile.document;
                        // Audio and plain-text previews render in the browser. Everything else
                        // goes to WebViewer Server, which must receive an https URL it can fetch.
                        // A blob: object URL fails with "URL root is not consistent".
                        const playInBrowser = isAudioFile(documentUrl) || isNativeTextPreviewFile(selectedFile);
                        const projectId = localStorage.getItem('project_id');
                        const wsId = resolveVdrWorkstreamId(selectedFile, workstream);
                        const canStreamViaApi =
                            playInBrowser &&
                            selectedFile?.id != null &&
                            /^\d+$/.test(String(selectedFile.id)) &&
                            wsId &&
                            projectId;

                        if (canStreamViaApi) {
                            try {
                                const objectUrl = await createVdrDocumentObjectUrl(selectedFile, { workstream });
                                blobObjectUrlRef.current = objectUrl;
                                if (isAudioFile(documentUrl)) {
                                    setIsAudio(true);
                                }
                                setFileUrl(objectUrl);
                            } catch (error) {
                                console.error('Failed to load document via API stream:', error);
                                setPdfLoading(false);
                                setUnsupportedFile(true);
                            }
                        } else if (documentUrl.includes('.blob.core.windows.net')) {
                            try {
                                const authenticatedUrl = await dispatch(GetAuthenticatedDocUrl(documentUrl)).unwrap();
                                setFileUrl(authenticatedUrl);
                            } catch (error) {
                                console.error('Failed to authorize document for WebViewer:', error);
                                setPdfLoading(false);
                            }
                        } else {
                            if (isAudioFile(documentUrl)) {
                                setIsAudio(true);
                            }
                            setFileUrl(documentUrl);
                        }
                    } else {
                        // No document file available - this should be handled as link file
                        setPdfLoading(false);
                    }
                }
            };
            efectFunction();
        }
        return () => {
            if (blobObjectUrlRef.current) {
                URL.revokeObjectURL(blobObjectUrlRef.current);
                blobObjectUrlRef.current = null;
            }
        };
    }, [selectedFile, dispatch, handleCheckVideo, checkIsLinkFile, workstream]);
    return (
        <>
            <DoubleConfirmationModal isMenuChild={true} handleAction={handleDeleteFile} message={'Deleting a Folder will also delete all files in the folder'}>
                <small ref={deleteModalRef}></small>
            </DoubleConfirmationModal>
            <OverlayDrawer
                size="full"
                position="bottom"
                open={openModal}
                onOpenChange={(_, state) => {
                    setOpenModal(state.open);
                    if (!state.open) {
                        citationHighlightRef.current = null;
                        onCitationHighlightConsumed?.();
                    }
                }}>
                {openModal && (
                    <>
                        <div className={styles.FVHeader}>
                            <div className="d-flex align-items-center">
                                {selectedFile?.document_name && (
                                    <p className={styles.FVfilePath}>
                                        {selectedFile?.document_name}
                                        <Menu positioning="below-start">
                                            <MenuTrigger disableButtonEnhancement>
                                                <MoreVertical16Regular className="ms-2" color="white" />
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem onClick={() => setOpenFileInfo(true)} icon={<Info16Regular />}>
                                                        Get Info
                                                    </MenuItem>
                                                    <MenuItem onClick={handleShareUrl} icon={<Link16Regular />} disabled={shareUrlLoading}>
                                                        Copy URL
                                                    </MenuItem>

                                                    {(isProjectAdmin ||
                                                        selectedFile?.source === 'project_mail_attachment' ||
                                                        selectedFile?.source === 'tent_chat_attachment' ||
                                                        selectedFile?.can_download_original ||
                                                        selectedFile?.can_download_watermarked) && (
                                                        <>
                                                            <MenuDivider />
                                                            <MenuItem onClick={handleDownloadFile} icon={<ArrowDownload16Regular />}>
                                                                Download File
                                                            </MenuItem>
                                                        </>
                                                    )}

                                                    {selectedFile?.source !== 'tent_chat_attachment' ? (
                                                        <>
                                                            <MenuDivider />
                                                            <MenuItem onClick={() => deleteModalRef?.current?.click()} icon={<Delete16Regular />}>
                                                                Delete File
                                                            </MenuItem>
                                                        </>
                                                    ) : null}
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    </p>
                                )}
                            </div>

                            <div className="d-flex align-items-center">
                                {folderFiles && (
                                    <div>
                                        <button disabled={selectedFileIndex === 0} onClick={() => handleNavigateFiles('prev')} className={styles.FVdirectionalBtn + ' ms-0'}>
                                            Previous
                                        </button>
                                        <span className={styles.FVfileCount}>
                                            {selectedFileIndex + 1} / {folderFiles?.length}
                                        </span>
                                        <button
                                            onClick={() => handleNavigateFiles('next')}
                                            disabled={selectedFileIndex === folderFiles?.length - 1}
                                            className={styles.FVdirectionalBtn}>
                                            Next
                                        </button>
                                    </div>
                                )}
                                {user?.has_diligence_tools && (
                                    <div className="me-4">
                                        <QuickAddMenu setObsModal={setObsModal} setQuestionModal={setQuestionModal} setTaskModal={setTaskModal} setNotepadModal={setNotepadModal} />
                                    </div>
                                )}

                                <Dismiss20Regular role="button" color="white" onClick={handleClose} />
                            </div>
                        </div>
                        <div className="row w-100 p-0 m-0">
                            <div className={`col-sm-12 col-md-${openFileInfo ? 9 : 12} p-0`}>
                                <div className={styles.docContainer}>
                                    {/* getYouTubeId */}
                                    {isVideo && (
                                        <div>
                                            {fileUrl?.includes('youtube') || fileUrl?.includes('youtu.be') ? (
                                                <iframe
                                                    width="100%"
                                                    height="70vh"
                                                    src={`https://www.youtube.com/embed/${getYouTubeId(fileUrl)}`}
                                                    frameborder="0"
                                                    title="youtube-vid"
                                                    allowfullscreen></iframe>
                                            ) : (
                                                <video
                                                    src={fileUrl}
                                                    controls
                                                    style={{
                                                        height: '70vh',
                                                        width: '100%',
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                    {isAudio && fileUrl && (
                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{
                                                height: '70vh',
                                                width: '100%',
                                            }}>
                                            <audio controls controlsList="nodownload">
                                                <source src={fileUrl} />
                                                Your browser does not support the audio element.
                                            </audio>
                                        </div>
                                    )}
                                    {isLinkFile && (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center"
                                            style={{
                                                height: '70vh',
                                                width: '100%',
                                                gap: '16px',
                                            }}>
                                            <p style={{ fontSize: '14px', color: 'var(--Brand-Primary)', margin: 0 }}>{selectedFile?.document_name}</p>
                                            <button
                                                onClick={handleOpenLink}
                                                style={{
                                                    padding: '5px 12px',
                                                    fontSize: '13px',
                                                    // fontWeight: '600',
                                                    backgroundColor: 'var(--Brand-Secondary)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                }}
                                                onMouseOver={e => {
                                                    e.target.style.opacity = '0.9';
                                                }}
                                                onMouseOut={e => {
                                                    e.target.style.opacity = '1';
                                                }}>
                                                <Open16Regular />
                                                Open Link in New Tab
                                            </button>
                                        </div>
                                    )}
                                    {isNativeTextPreview && pdfLoading && !nativeTextContent && (
                                        <div className={styles.loaderBody}>
                                            <AiOutlineLoading3Quarters size={50} className="rotateLoader" />
                                        </div>
                                    )}
                                    {isNativeTextPreview && nativeTextContent && (
                                        <div className={styles.nativeTextPreview}>
                                            {nativeTextMode === 'html' ? (
                                                <iframe title={selectedFile?.document_name} sandbox="" srcDoc={nativeTextContent} className={styles.nativeTextPreviewFrame} />
                                            ) : (
                                                <pre className={styles.nativeTextPreviewPlain}>{nativeTextContent}</pre>
                                            )}
                                        </div>
                                    )}
                                    {unsupportedFile && !isLinkFile && !isVideo && !isAudio && (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center"
                                            style={{
                                                height: '70vh',
                                                width: '100%',
                                                gap: '8px',
                                            }}>
                                            <p style={{ fontSize: '14px', color: 'var(--Brand-Primary)', margin: 0, maxWidth: '520px', textAlign: 'center' }}>
                                                {previewErrorMessage || 'This file type cannot be previewed.'}
                                            </p>
                                            {selectedFile?.document_name && (
                                                <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>{selectedFile.document_name}</p>
                                            )}
                                        </div>
                                    )}
                                    <div
                                        key={selectedFile?.id || fileUrl || 'viewer'}
                                        style={{ visibility: (isVideo || isAudio || isLinkFile || unsupportedFile || isNativeTextPreview) && 'hidden' }}
                                        className={'webviewer'}
                                        ref={viewer}>
                                        {pdfLoading && (
                                            <div className={styles.loaderBody}>
                                                <AiOutlineLoading3Quarters size={50} className="rotateLoader" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {openFileInfo && (
                                <div className="col-sm-12 col-md-3 p-0">
                                    <FileInfoSidebar setFileInfoSidebar={setOpenFileInfo} setShowSidebar={() => {}} selectedFile={selectedFile} setSelectedFile={() => {}} />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </OverlayDrawer>
            <div style={{ zIndex: 1000000000000 }}>
                <NotepadSidebar show={notepadModal} setShow={setNotepadModal} />
                <ObservationFormSidebar show={obsModal} setShow={setObsModal} />
                <PostInformationRequestSidebar show={questionModal} setShow={setQuestionModal} />
                <TaskFormSidebar show={taskModal} setShow={setTaskModal} />
            </div>
        </>
    );
};
export default FileViewerModal;
